import React from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolveImg, API } from "../lib/api";
import { toast } from "sonner";
import useSEO from "../lib/useSEO";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";

/** Minimal markdown-ish renderer: paragraphs, ##/### headings, **bold**, lists, links. */
function RenderContent({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="list-disc pl-6 my-4 space-y-1 text-zinc-700">
          {listBuf.map((it, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inline(it) }} />)}
        </ul>
      );
      listBuf = [];
    }
  };
  const inline = (s) =>
    s
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener" class="underline hover:text-[#031833]">$1</a>');
  lines.forEach((raw, i) => {
    const l = raw.trimEnd();
    if (/^###\s+/.test(l)) { flushList(); blocks.push(<h3 key={i} className="font-display text-xl tracking-tighter mt-6 mb-2">{l.replace(/^###\s+/, "")}</h3>); return; }
    if (/^##\s+/.test(l)) { flushList(); blocks.push(<h2 key={i} className="font-display text-2xl tracking-tighter mt-8 mb-3">{l.replace(/^##\s+/, "")}</h2>); return; }
    if (/^[-*]\s+/.test(l)) { listBuf.push(l.replace(/^[-*]\s+/, "")); return; }
    if (!l.trim()) { flushList(); return; }
    flushList();
    blocks.push(<p key={i} className="text-zinc-700 leading-relaxed my-3" dangerouslySetInnerHTML={{ __html: inline(l) }} />);
  });
  flushList();
  return <div>{blocks}</div>;
}

function UnlockDialog({ open, onOpenChange, onUnlocked, slug, reportTitle }) {
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", company: "" });
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/market-reports/${slug}/unlock`, form);
      onUnlocked(res.data);
      toast.success("Unlocked! Full report is now available below.");
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unlock failed — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="market-unlock-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tighter text-2xl">UNLOCK FULL REPORT</DialogTitle>
          <DialogDescription className="font-mono-tech text-xs">
            {reportTitle ? `“${reportTitle}”` : "Get the full analysis & downloadable PDF."} <br />
            Enter your details — no spam, just the report.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input data-testid="unlock-name" placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input data-testid="unlock-email" type="email" placeholder="Work email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input data-testid="unlock-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Input data-testid="unlock-company" placeholder="Company (optional)" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
          <DialogFooter className="gap-2 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="font-mono-tech text-xs uppercase px-4 py-2 border border-zinc-300 hover:bg-zinc-100"
            >Cancel</button>
            <button
              type="submit"
              data-testid="unlock-submit"
              disabled={submitting}
              className="font-mono-tech text-xs uppercase px-4 py-2 bg-[#031833] text-white hover:bg-[#0A2455] disabled:opacity-50"
            >{submitting ? "Unlocking…" : "Unlock report →"}</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function MarketReportDetail() {
  const { slug } = useParams();
  const [report, setReport] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [dialog, setDialog] = React.useState(false);
  const [notFound, setNotFound] = React.useState(false);

  useSEO({
    title: report ? `${report.title} | Market Analytics — WarehousesUAE` : "Market Report | WarehousesUAE",
    description: report?.summary || "Industrial real estate market report by WarehousesUAE.",
    canonical: `/market-analytics/${slug}`,
    ogImage: report?.cover_image_url ? resolveImg(report.cover_image_url) : undefined,
  });

  const fetchReport = React.useCallback(() => {
    api
      .get(`/market-reports/${slug}`)
      .then((r) => {
        setReport(r.data);
        setLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [slug]);

  React.useEffect(fetchReport, [fetchReport]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center font-mono-tech text-xs text-zinc-500">Loading…</div>;
  if (notFound || !report) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
        <div className="font-display text-4xl tracking-tighter mb-3">Report not found</div>
        <Link to="/market-analytics" className="font-mono-tech text-xs uppercase border border-zinc-900 px-4 py-2 hover:bg-zinc-900 hover:text-white">← Back to all reports</Link>
      </div>
    );
  }

  const pdfHref = report.pdf_url ? `${API}${report.pdf_url}`.replace("/api/api/", "/api/") : null;
  // resolveImg returns absolute; but pdf_url stored as /api/uploads/{id}; build with resolveImg
  const pdfFullUrl = report.pdf_url ? resolveImg(report.pdf_url) : null;

  return (
    <article className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-[#031833] text-white">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8 py-16">
          <Link to="/market-analytics" data-testid="back-to-market" className="font-mono-tech text-[10px] uppercase text-[#FFCC00] hover:text-white mb-4 inline-block">← All reports</Link>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="font-mono-tech text-[10px] uppercase text-[#FFCC00] bg-white/10 px-2 py-1">{report.category}</span>
            <span className="font-mono-tech text-[10px] text-zinc-400">
              {new Date(report.published_at).toLocaleDateString("en-AE", { year: "numeric", month: "long", day: "numeric" })}
            </span>
          </div>
          <h1 data-testid="report-title" className="font-display text-4xl md:text-5xl tracking-tighter mb-4 max-w-3xl">{report.title}</h1>
          <p className="font-mono-tech text-sm text-zinc-300 max-w-2xl leading-relaxed">{report.summary}</p>
          <div className="flex flex-wrap gap-2 mt-5">
            {(report.tags || []).map((t) => (
              <span key={t} className="font-mono-tech text-[10px] text-zinc-300 border border-zinc-700 px-2 py-0.5">#{t}</span>
            ))}
          </div>
        </div>
      </section>

      {report.cover_image_url && (
        <div className="max-w-[1100px] mx-auto px-4 md:px-8 -mt-10 mb-10 relative">
          <img src={resolveImg(report.cover_image_url)} alt={report.title} className="w-full aspect-[16/8] object-cover border-4 border-white shadow-xl" />
        </div>
      )}

      <div className="max-w-[820px] mx-auto px-4 md:px-8 pb-20">
        {/* Free preview */}
        {report.preview_content && (
          <section data-testid="report-preview" className="prose-content">
            <RenderContent text={report.preview_content} />
          </section>
        )}

        {/* Gated or unlocked content */}
        {report.gated ? (
          <div data-testid="market-gate" className="my-12 bg-zinc-50 border-2 border-dashed border-[#031833] p-8 text-center">
            <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-2">// FULL REPORT</div>
            <h2 className="font-display text-3xl tracking-tighter mb-3">UNLOCK THE FULL ANALYSIS</h2>
            <p className="text-zinc-600 text-sm max-w-md mx-auto mb-6">
              Enter your details to access the full report content
              {report.pdf_url ? " and download the PDF." : "."}
            </p>
            <button
              data-testid="open-unlock-dialog"
              onClick={() => setDialog(true)}
              className="font-mono-tech text-xs uppercase px-6 py-3 bg-[#031833] text-white hover:bg-[#0A2455]"
            >
              🔓 Unlock report →
            </button>
          </div>
        ) : (
          <>
            {report.body_content && (
              <section data-testid="report-body" className="my-8">
                <RenderContent text={report.body_content} />
              </section>
            )}
            {pdfFullUrl && (
              <div className="my-10 flex items-center justify-between gap-4 bg-zinc-50 border border-zinc-200 p-5">
                <div>
                  <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-1">// DOWNLOAD</div>
                  <div className="font-display text-lg tracking-tighter">Full PDF Report</div>
                </div>
                <a
                  href={pdfFullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="download-pdf"
                  className="font-mono-tech text-xs uppercase px-5 py-3 bg-[#031833] text-white hover:bg-[#0A2455]"
                >
                  ⬇ Download PDF
                </a>
              </div>
            )}
          </>
        )}
      </div>

      <UnlockDialog
        open={dialog}
        onOpenChange={setDialog}
        slug={slug}
        reportTitle={report.title}
        onUnlocked={(unlocked) => setReport(unlocked)}
      />
    </article>
  );
}
